import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import dayjs from 'dayjs';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PercentIcon from '@mui/icons-material/Percent';
import StorefrontIcon from '@mui/icons-material/Storefront';

import PageHeader from '../../../components/PageHeader/PageHeader';
import MonthYearSelector from '../../../components/Reports/Sales/MonthYearSelector';
import { getProfitabilityDashboardData } from '../../../services/ReportsProfitabilityService';
import { formatMoney } from '../../../utils/currency';

import useReportCurrency from '../../../hooks/useReportCurrency';
import ReportCurrencyPicker from '../../../components/Currency/ReportCurrencyPicker';
const money = (value = 0) => `${formatMoney(Number(value || 0))}`;
const percent = (value = 0) => `${Number(value || 0).toLocaleString('es-UY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
const gridValue = (valueOrParams) => (valueOrParams && typeof valueOrParams === 'object' && 'value' in valueOrParams
  ? valueOrParams.value
  : valueOrParams);
const gridMoney = (valueOrParams) => money(gridValue(valueOrParams));
const gridPercent = (valueOrParams) => percent(gridValue(valueOrParams));

const COLORS = ['#1976d2', '#2e7d32', '#ed6c02', '#9c27b0', '#d32f2f', '#00838f', '#6d4c41', '#455a64'];

const KPI_META = {
  netSales: { title: 'Ventas netas', icon: <MonetizationOnIcon />, tone: 'neutral' },
  costOfGoodsSold: { title: 'Costo productos', icon: <Inventory2Icon />, tone: 'neutral' },
  grossProfit: { title: 'Ganancia bruta', icon: <TrendingUpIcon />, tone: 'profit' },
  assignedExpenses: { title: 'Gastos asignados', icon: <ReceiptLongIcon />, tone: 'expense' },
  realProfit: { title: 'Rentabilidad real', icon: <AccountBalanceWalletIcon />, tone: 'profit' },
  margins: { title: 'Márgenes', icon: <PercentIcon />, tone: 'neutral' },
};

const getToneColor = (tone, value) => {
  if (tone === 'profit') return value < 0 ? '#d32f2f' : '#2e7d32';
  if (tone === 'expense') return '#ed6c02';
  return '#1976d2';
};

const KpiCard = ({ title, value, rawValue, icon, tone, delta, loading, secondary }) => {
  const color = getToneColor(tone, Number(rawValue || 0));
  const isPositiveDelta = Number(delta || 0) >= 0;

  return (
    <Paper
      elevation={2}
      sx={{
        height: 94,
        p: 2,
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      {loading ? (
        <Stack spacing={1}>
          <Skeleton width="45%" />
          <Skeleton width="70%" height={34} />
          <Skeleton width="35%" />
        </Stack>
      ) : (
        <Stack spacing={1}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary" fontWeight={700}>{title}</Typography>
            <Box sx={{ color, display: 'flex' }}>{icon}</Box>
          </Stack>
          <Typography variant="h5" fontWeight={700} sx={{ color, lineHeight: 1.15 }}>{value}</Typography>
          {secondary ? (
            <Typography variant="caption" color="text.secondary">{secondary}</Typography>
          ) : (
            <Chip
              size="small"
              variant="outlined"
              color={isPositiveDelta ? 'success' : 'error'}
              icon={isPositiveDelta ? <TrendingUpIcon /> : <TrendingDownIcon />}
              label={`${isPositiveDelta ? '+' : ''}${money(delta)} vs mes anterior`}
              sx={{
                alignSelf: 'flex-start',
                maxWidth: '100%',
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
              }}
            />
          )}
        </Stack>
      )}
    </Paper>
  );
};

const ChartCard = ({ title, children, loading, height = 300 }) => (
  <Paper elevation={2} sx={{ p: 2, borderRadius: 1, height: height + 64, overflow: 'hidden' }}>
    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>{title}</Typography>
    {loading ? (
      <Skeleton variant="rectangular" height={height} />
    ) : (
      <Box sx={{ height }}>{children}</Box>
    )}
  </Paper>
);

const EmptyChart = ({ label }) => (
  <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', color: 'text.secondary' }}>
    <Typography variant="body2">{label}</Typography>
  </Stack>
);

const METRIC_COLORS = {
  netSales: '#1976d2',
  costOfGoodsSold: '#455a64',
  grossProfit: '#2e7d32',
  assignedExpenses: '#ed6c02',
  realProfit: '#6a1b9a',
};

const ProfitabilityReports = () => {
  const [reportCurrency, setReportCurrency] = useReportCurrency();
  const isSuperAdmin = localStorage.getItem('isSuperAdmin') === 'true';
  const [month, setMonth] = useState(dayjs().month() + 1);
  const [year, setYear] = useState(dayjs().year());
  const [rut, setRut] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profitabilityScope, setProfitabilityScope] = useState('COMPANY');
  const [selectedLocationId, setSelectedLocationId] = useState('');

  useEffect(() => {
    let ignore = false;

    const fetchData = async () => {
      if (profitabilityScope === 'LOCATION' && !selectedLocationId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const result = await getProfitabilityDashboardData({
          month,
          year,
          rut: isSuperAdmin ? rut : undefined,
          locationId: profitabilityScope === 'LOCATION' ? selectedLocationId : undefined,
        });
        if (!ignore) {
          setData(result);
          if (isSuperAdmin && rut && !result.availableRuts?.includes(rut)) {
            setRut('');
          }
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    fetchData();
    return () => { ignore = true; };
  }, [month, year, rut, isSuperAdmin, profitabilityScope, selectedLocationId, reportCurrency]);

  const summary = data?.summary || {};
  const comparison = summary.comparison || {};
  const locationRows = data?.locationBreakdown || [];
  const expenseCategories = data?.expenseCategoryBreakdown || [];
  const branchOptions = data?.branches || [];
  const hasExpenseCategories = expenseCategories.length > 0;
  const hasLocations = locationRows.length > 0;
  const hasBranchOptions = branchOptions.length > 0;

  useEffect(() => {
    if (profitabilityScope !== 'LOCATION') return;

    const selectedStillExists = branchOptions.some((row) => String(row.id) === String(selectedLocationId));
    if (!selectedStillExists) {
      setSelectedLocationId(branchOptions[0]?.id || '');
    }
  }, [branchOptions, profitabilityScope, selectedLocationId]);

  const kpis = [
    { key: 'netSales', rawValue: summary.netSales, value: money(summary.netSales), delta: comparison.netSalesDelta },
    { key: 'costOfGoodsSold', rawValue: summary.costOfGoodsSold, value: money(summary.costOfGoodsSold), secondary: 'Costo vigente aplicado a productos vendidos' },
    { key: 'grossProfit', rawValue: summary.grossProfit, value: money(summary.grossProfit), delta: comparison.grossProfitDelta },
    { key: 'assignedExpenses', rawValue: summary.assignedExpenses, value: money(summary.assignedExpenses), secondary: 'Devengados por vencimiento del período' },
    { key: 'realProfit', rawValue: summary.realProfit, value: money(summary.realProfit), delta: comparison.realProfitDelta },
    { key: 'margins', rawValue: summary.realMarginPercent, value: percent(summary.realMarginPercent), secondary: `Bruto ${percent(summary.grossMarginPercent)} · Real ${percent(summary.realMarginPercent)}` },
  ];

  const locationColumns = useMemo(() => [
    { field: 'locationName', headerName: 'Sucursal', flex: 1, minWidth: 115 },
    { field: 'ticketCount', headerName: 'Tickets', type: 'number', width: 80, align: 'center', headerAlign: 'center' },
    { field: 'averageTicket', headerName: 'Ticket prom.', type: 'number', width: 110, valueFormatter: gridMoney, align: 'center', headerAlign: 'center' },
    { field: 'netSales', headerName: 'Facturado', type: 'number', width: 115, valueFormatter: gridMoney, align: 'center', headerAlign: 'center' },
    { field: 'assignedExpenses', headerName: 'Fijos', type: 'number', width: 105, valueFormatter: gridMoney, align: 'center', headerAlign: 'center' },
    { field: 'costOfGoodsSold', headerName: 'Variables', type: 'number', width: 120, valueFormatter: gridMoney, align: 'center', headerAlign: 'center' },
    { field: 'realProfit', headerName: 'Resultado', type: 'number', width: 120, valueFormatter: gridMoney, align: 'center', headerAlign: 'center' },
    {
      field: 'realMarginPercent',
      headerName: 'Resultado %',
      type: 'number',
      width: 105,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (Number(params.row.netSales || 0) === 0 ? '—' : gridPercent(params.value)),
    },
  ], []);

  const selectedLocation = branchOptions.find((row) => String(row.id) === String(selectedLocationId));
  const focusLabel = profitabilityScope === 'LOCATION' && selectedLocation ? selectedLocation.name : 'Empresa global';
  const profitabilityComposition = [
    { key: 'netSales', name: 'Ventas', value: Number(summary.netSales || 0), color: METRIC_COLORS.netSales },
    { key: 'costOfGoodsSold', name: 'Costo', value: Number(summary.costOfGoodsSold || 0), color: METRIC_COLORS.costOfGoodsSold },
    { key: 'grossProfit', name: 'Ganancia bruta', value: Number(summary.grossProfit || 0), color: METRIC_COLORS.grossProfit },
    { key: 'assignedExpenses', name: 'Gastos', value: Number(summary.assignedExpenses || 0), color: METRIC_COLORS.assignedExpenses },
    {
      key: 'realProfit',
      name: 'Rentabilidad real',
      value: Number(summary.realProfit || 0),
      color: Number(summary.realProfit || 0) < 0 ? '#d32f2f' : METRIC_COLORS.realProfit,
    },
  ];
  const hasProfitabilityComposition = profitabilityComposition.some((item) => item.value !== 0);

  return (
    <Box p={3}>
      <PageHeader title="Rentabilidad" subtitle="Ventas, costos, gastos y ganancia real" />
      <ReportCurrencyPicker currency={reportCurrency} onChange={setReportCurrency} />

      <Paper
        variant="outlined"
        sx={{
          mt: 2,
          p: 2,
          borderRadius: 1.5,
          backgroundColor: 'background.paper',
        }}
      >
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Box sx={{ '& .MuiTextField-root': { width: '100%' } }}>
              <MonthYearSelector month={month} setMonth={setMonth} year={year} setYear={setYear} />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <FormControl size="small" fullWidth>
              <InputLabel>Vista</InputLabel>
              <Select
                label="Vista"
                value={profitabilityScope}
                onChange={(event) => setProfitabilityScope(event.target.value)}
              >
                <MenuItem value="COMPANY">Empresa global</MenuItem>
                <MenuItem value="LOCATION" disabled={!hasBranchOptions}>Sucursal</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <FormControl size="small" fullWidth disabled={profitabilityScope !== 'LOCATION'}>
              <InputLabel>Sucursal</InputLabel>
              <Select
                label="Sucursal"
                value={profitabilityScope === 'LOCATION' ? selectedLocationId : ''}
                onChange={(event) => setSelectedLocationId(event.target.value)}
              >
                {profitabilityScope !== 'LOCATION' ? (
                  <MenuItem value="">Todas las sucursales</MenuItem>
                ) : (
                  branchOptions.map((row) => (
                    <MenuItem key={row.id} value={row.id}>{row.name}</MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Grid>
          {isSuperAdmin && (
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <FormControl size="small" fullWidth>
                <InputLabel>RUT</InputLabel>
                <Select label="RUT" value={rut} onChange={(event) => setRut(event.target.value)}>
                  <MenuItem value="">Todos</MenuItem>
                  {(data?.availableRuts || []).map((item) => (
                    <MenuItem key={item} value={item}>{item}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>
      </Paper>

      <Grid container spacing={2} mt={0} alignItems="flex-start">
        {kpis.map((item) => {
          const meta = KPI_META[item.key];
          return (
            <Grid item xs={12} sm={6} md={4} key={item.key}>
              <KpiCard
                title={meta.title}
                value={item.value}
                rawValue={item.rawValue}
                icon={meta.icon}
                tone={meta.tone}
                delta={item.delta}
                secondary={item.secondary}
                loading={loading}
              />
            </Grid>
          );
        })}
      </Grid>

      <Grid container spacing={2} mt={2}>
        <Grid item xs={12} md={8}>
          <ChartCard title="Evolución mensual" loading={loading}>
            {(data?.monthlySeries || []).length === 0 ? (
              <EmptyChart label="No hay datos para graficar" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.monthlySeries} margin={{ top: 8, right: 16, bottom: 0, left: 18 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" />
                  <YAxis width={86} tickFormatter={(value) => `${formatMoney(Number(value))}`} />
                  <Tooltip formatter={(value) => money(value)} />
                  <Legend />
                  <Area type="monotone" dataKey="netSales" name="Ventas" stroke="#1976d2" fill="#1976d2" fillOpacity={0.12} />
                  <Area type="monotone" dataKey="grossProfit" name="Ganancia bruta" stroke="#2e7d32" fill="#2e7d32" fillOpacity={0.1} />
                  <Area type="monotone" dataKey="assignedExpenses" name="Gastos" stroke="#ed6c02" fill="#ed6c02" fillOpacity={0.08} />
                  <Area type="monotone" dataKey="realProfit" name="Rentabilidad real" stroke="#6a1b9a" fill="#6a1b9a" fillOpacity={0.1} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <ChartCard title="Gastos por categoría" loading={loading}>
            {!hasExpenseCategories ? (
              <EmptyChart label="No hay gastos asignados" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={expenseCategories} dataKey="amount" nameKey="category" outerRadius={95} label>
                    {expenseCategories.map((entry, index) => (
                      <Cell key={entry.category} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => money(value)} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Grid>
      </Grid>

      <Grid container spacing={2} mt={2}>
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 2, borderRadius: 1, height: 344, overflow: 'hidden' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.5} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>Composición de rentabilidad</Typography>
                {!loading && (
                  <Typography variant="caption" color="text.secondary">{focusLabel}</Typography>
                )}
              </Box>
            </Stack>
            {loading ? (
              <Skeleton variant="rectangular" height={260} />
            ) : !hasProfitabilityComposition ? (
              <Box sx={{ height: 260 }}>
                <EmptyChart label="No hay datos para graficar" />
              </Box>
            ) : (
              <Box sx={{ height: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={profitabilityComposition} margin={{ top: 12, right: 16, bottom: 0, left: 18 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis width={86} tickFormatter={(value) => `${formatMoney(Number(value))}`} />
                    <Tooltip formatter={(value) => money(value)} />
                    <Bar dataKey="value" name={focusLabel} radius={[4, 4, 0, 0]}>
                      {profitabilityComposition.map((entry) => (
                        <Cell key={entry.key} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {hasLocations && (
        <Grid container spacing={2} mt={2} mb={5}>
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 2, borderRadius: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <StorefrontIcon color="primary" />
                <Typography variant="subtitle1" fontWeight={700}>Resumen por sucursal</Typography>
              </Stack>
              <DataGrid
                autoHeight
                rows={locationRows}
                columns={locationColumns}
                getRowId={(row) => row.locationId}
                loading={loading}
                disableRowSelectionOnClick
                pageSizeOptions={[10, 25, 50]}
                initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              />
            </Paper>
          </Grid>
        </Grid>
      )}

    </Box>
  );
};

export default ProfitabilityReports;
